import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandShell from "../components/BrandShell";
import SEO from "../components/SEO";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Ticket, Plus, Copy, Check, Trash2, Power, RefreshCcw, Users, Sparkles, Loader2, X,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso); }
}

function StatPill({ label, value, icon: Icon, tone }) {
  return (
    <div className="glass rounded-2xl p-4 min-w-[130px]" data-testid={`promo-stat-${label.toLowerCase().replace(/\s/g,"-")}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-1.5">
        <Icon className={`h-3.5 w-3.5 ${tone}`} /> {label}
      </div>
      <div className="font-display font-black text-2xl tabular-nums text-white">{value}</div>
    </div>
  );
}

const DEFAULT_FORM = {
  code: "",
  plan: "brand_pro",
  duration_months: 3,
  max_uses: 100,
  is_active: true,
  notes: "",
  expires_at: "",
};

export default function AdminPromo() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [copiedCode, setCopiedCode] = useState(null);
  const [redemptions, setRedemptions] = useState(null); // { code_id, list }
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/promo");
      setRows(r.data?.codes || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load promo codes");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    const total_codes = rows.length;
    const active = rows.filter((r) => r.is_active).length;
    const redemptions_count = rows.reduce((s, r) => s + (r.used_count || 0), 0);
    return { total_codes, active, redemptions_count };
  }, [rows]);

  const create = async () => {
    if (!form.code || form.code.length < 4) { toast.error("Code must be at least 4 characters"); return; }
    setCreating(true);
    try {
      const payload = {
        code: form.code.toUpperCase().trim(),
        plan: form.plan,
        duration_months: Number(form.duration_months) || 1,
        max_uses: Number(form.max_uses) || 0,
        is_active: !!form.is_active,
        notes: form.notes || "",
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      await api.post("/admin/promo", payload);
      toast.success(`Code ${payload.code} created`);
      setForm(DEFAULT_FORM);
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally { setCreating(false); }
  };

  const toggle = async (row) => {
    try {
      await api.patch(`/admin/promo/${row.id}`, { is_active: !row.is_active });
      toast.success(`Code ${row.code} ${row.is_active ? "paused" : "reactivated"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete code ${row.code}? This cannot be undone. Redemptions are preserved.`)) return;
    try {
      await api.delete(`/admin/promo/${row.id}`);
      toast.success(`Code ${row.code} deleted`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const openRedemptions = async (row) => {
    setRedemptions({ code_id: row.id, code: row.code, list: null });
    try {
      const r = await api.get(`/admin/promo/${row.id}/redemptions`);
      setRedemptions({ code_id: row.id, code: row.code, list: r.data?.redemptions || [] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code); setTimeout(() => setCopiedCode(null), 1800);
      toast.success(`Copied ${code}`);
    });
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <BrandShell>
      <SEO title="Promo Codes · Admin" description="Create and manage subscription promo codes." path="/admin/promo" />
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 py-8 space-y-6" data-testid="admin-promo-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
              <Ticket className="h-3.5 w-3.5 text-nexus-pink" /> Admin
            </div>
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">Promo codes</h1>
            <p className="text-zinc-400 mt-2 text-sm">Create time-limited Pro upgrades for outreach. Redemptions stack extra months on top of any existing pro window.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={load} variant="outline" className="rounded-full border-white/10 text-white hover:bg-white/5" data-testid="promo-refresh-btn">
              <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button
              onClick={() => setShowForm((v) => !v)}
              className="nexus-gradient text-white border-0 rounded-full font-semibold"
              data-testid="promo-new-btn"
            >
              {showForm ? <><X className="h-4 w-4 mr-2" /> Cancel</> : <><Plus className="h-4 w-4 mr-2" /> New code</>}
            </Button>
          </div>
        </div>

        <div className="flex gap-3 flex-wrap">
          <StatPill label="Total codes" value={totals.total_codes} icon={Ticket} tone="text-nexus-pink" />
          <StatPill label="Active" value={totals.active} icon={Power} tone="text-emerald-300" />
          <StatPill label="Redemptions" value={totals.redemptions_count} icon={Users} tone="text-nexus-blue" />
        </div>

        {/* Create form */}
        {showForm && (
          <div className="glass rounded-2xl p-6 space-y-4" data-testid="promo-create-form">
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Code</label>
                <Input
                  value={form.code}
                  onChange={(e) => setField("code", e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 32))}
                  placeholder="LAUNCH3M"
                  className="bg-white/[0.02] border-white/10 text-white uppercase font-mono tracking-wider"
                  data-testid="promo-form-code"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Plan</label>
                <Select value={form.plan} onValueChange={(v) => setField("plan", v)}>
                  <SelectTrigger className="bg-white/[0.02] border-white/10 text-white" data-testid="promo-form-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brand_pro">Brand Pro</SelectItem>
                    <SelectItem value="creator_pro">Creator Pro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Duration (months)</label>
                <Input
                  type="number"
                  min={1}
                  max={24}
                  value={form.duration_months}
                  onChange={(e) => setField("duration_months", e.target.value)}
                  className="bg-white/[0.02] border-white/10 text-white"
                  data-testid="promo-form-duration"
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Max uses (0 = ∞)</label>
                <Input
                  type="number"
                  min={0}
                  value={form.max_uses}
                  onChange={(e) => setField("max_uses", e.target.value)}
                  className="bg-white/[0.02] border-white/10 text-white"
                  data-testid="promo-form-maxuses"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Expires (optional)</label>
                <Input
                  type="date"
                  value={form.expires_at}
                  onChange={(e) => setField("expires_at", e.target.value)}
                  className="bg-white/[0.02] border-white/10 text-white"
                  data-testid="promo-form-expires"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 h-10 text-sm text-white cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setField("is_active", e.target.checked)}
                    className="h-4 w-4"
                    data-testid="promo-form-active"
                  />
                  Active immediately
                </label>
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Notes (internal)</label>
              <Input
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value.slice(0, 280))}
                placeholder="Launch outreach · Q1 · Instagram DMs"
                className="bg-white/[0.02] border-white/10 text-white"
                data-testid="promo-form-notes"
              />
            </div>
            <div className="flex justify-end">
              <Button
                onClick={create}
                disabled={creating}
                className="nexus-gradient text-white border-0 rounded-full h-10 px-5 font-semibold disabled:opacity-60"
                data-testid="promo-form-submit"
              >
                {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</> : <><Sparkles className="h-4 w-4 mr-2" /> Create code</>}
              </Button>
            </div>
          </div>
        )}

        {/* Codes list */}
        {loading ? (
          <div className="glass rounded-2xl p-12 text-center text-zinc-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center">
            <Ticket className="h-6 w-6 mx-auto mb-3 text-zinc-500" />
            <div className="font-display text-xl mb-1">No promo codes yet</div>
            <p className="text-zinc-500 text-sm">Create your first code above.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="promo-codes-list">
            {rows.map((row) => {
              const usePct = row.max_uses > 0 ? Math.round((row.used_count / row.max_uses) * 100) : 0;
              return (
                <div
                  key={row.id}
                  className={`glass rounded-2xl p-5 flex items-center gap-4 flex-wrap ${!row.is_active ? "opacity-60" : ""}`}
                  data-testid={`promo-row-${row.code}`}
                >
                  <button
                    onClick={() => copyCode(row.code)}
                    className="text-left group"
                    data-testid={`promo-copy-${row.code}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-lg tracking-widest text-white">{row.code}</span>
                      {copiedCode === row.code ? <Check className="h-3.5 w-3.5 text-emerald-300" /> : <Copy className="h-3.5 w-3.5 text-zinc-500 group-hover:text-white transition-colors" />}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      {row.plan === "brand_pro" ? "Brand Pro" : "Creator Pro"} · {row.duration_months} months
                    </div>
                  </button>

                  <div className="flex-1 min-w-[180px]">
                    <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
                      <Users className="h-3 w-3" /> {row.used_count} / {row.max_uses || "∞"} redeemed
                    </div>
                    {row.max_uses > 0 && (
                      <div className="h-1 bg-white/5 rounded-full overflow-hidden mt-1 max-w-[220px]">
                        <div className="h-full nexus-gradient" style={{ width: `${usePct}%` }} />
                      </div>
                    )}
                    {row.notes && <div className="text-[11px] text-zinc-500 mt-1 truncate max-w-[280px]">{row.notes}</div>}
                  </div>

                  <div className="text-[11px] text-zinc-500 min-w-[120px]">
                    <div>Created</div>
                    <div className="text-zinc-400">{fmtDate(row.created_at)}</div>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      onClick={() => openRedemptions(row)}
                      variant="outline"
                      className="rounded-full border-white/10 text-white hover:bg-white/5 h-8"
                      data-testid={`promo-redemptions-${row.code}`}
                    >
                      Redemptions
                    </Button>
                    <button
                      onClick={() => toggle(row)}
                      className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${row.is_active ? "text-amber-300 hover:bg-amber-500/10" : "text-emerald-300 hover:bg-emerald-500/10"}`}
                      title={row.is_active ? "Pause" : "Activate"}
                      data-testid={`promo-toggle-${row.code}`}
                    >
                      <Power className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => remove(row)}
                      className="h-8 w-8 rounded-full flex items-center justify-center text-red-300 hover:bg-red-500/10 transition-colors"
                      title="Delete"
                      data-testid={`promo-delete-${row.code}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Redemption drawer */}
      {redemptions && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setRedemptions(null)}>
          <div className="glass rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="promo-redemptions-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Redemptions</div>
                <div className="font-display font-bold text-xl">{redemptions.code}</div>
              </div>
              <button onClick={() => setRedemptions(null)} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-white/5">
                <X className="h-4 w-4" />
              </button>
            </div>
            {redemptions.list === null ? (
              <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : redemptions.list.length === 0 ? (
              <div className="text-zinc-500 text-sm text-center py-8">No redemptions yet.</div>
            ) : (
              <ol className="space-y-2">
                {redemptions.list.map((r) => (
                  <li key={r.id} className="p-3 rounded-lg bg-white/[0.02] border border-white/5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-white text-[11px] truncate max-w-[280px]">{r.user_id}</span>
                      <span className="text-[10px] uppercase tracking-widest text-emerald-300">{r.user_role}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                      Granted {fmtDate(r.granted_at)} · until {fmtDate(r.granted_until)}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}
    </BrandShell>
  );
}
